/**
 * Driver.js wiring: start/restart a role tour, persist completion, track analytics.
 *
 * Completion is stored in localStorage per user + tour id.
 * ponytail: localStorage only — the flag is lost on a new device/browser.
 * Move it to a `tour_completed_at` on the user profile when that matters.
 */
import { driver, type Driver } from 'driver.js'
import { computed, h, render, type Component } from 'vue'
import {
    LayoutDashboard, Kanban, Building, Building2, DollarSign, Package, Target, MessageSquare,
    Megaphone, FileText, LifeBuoy, Settings, Activity, Zap, Compass, Search, Plus,
} from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { useUserStore } from '../stores/user'
import { trackEvent } from '../services/analytics'
import { tourForRole, type PreviewRow, type TourStep } from './tours'

export type TourTrigger = 'auto' | 'manual'

const doneKey = (tourId: string, userId: string) => `cc:tour:${tourId}:${userId}`

export function isTourDone(tourId: string, userId: string): boolean {
    try { return localStorage.getItem(doneKey(tourId, userId)) === 'done' } catch { return false }
}

export function markTourDone(tourId: string, userId: string, done = true) {
    try {
        if (done) localStorage.setItem(doneKey(tourId, userId), 'done')
        else localStorage.removeItem(doneKey(tourId, userId))
    } catch { /* storage unavailable — tour may show again next visit */ }
}

/** Sidebar is hidden below Tailwind's md breakpoint; the tour has nothing to point at. */
const MIN_TOUR_WIDTH = 768

const icons: Record<string, Component> = {
    LayoutDashboard, Kanban, Building, Building2, DollarSign, Package, Target, MessageSquare,
    Megaphone, FileText, LifeBuoy, Settings, Activity, Zap, Compass,
}

const mk = (cls: string, text = '') => {
    const d = document.createElement('div')
    d.className = cls
    if (text) d.textContent = text
    return d
}

function previewRows(preview: PreviewRow[]): HTMLElement[] {
    return preview.map(({ icon, label, active }) => {
        const r = mk('cc-tour-mock-row' + (active ? ' is-active' : ''))
        if (icons[icon]) render(h(icons[icon], { size: 15, 'aria-hidden': 'true' }), r)
        r.append(document.createTextNode(label))
        return r
    })
}

/** Miniature of the real sidebar beside a faint page skeleton. */
function sidebarMock(preview: PreviewRow[]): HTMLElement {
    const mock = mk('cc-tour-mock')
    const side = mk('cc-tour-mock-side')
    const logo = new Image()
    logo.src = document.documentElement.classList.contains('dark') ? '/assets/companion-on-dark.svg' : '/assets/companion-on-light.svg'
    logo.alt = ''
    logo.className = 'cc-tour-mock-logo'
    side.append(logo, ...previewRows(preview))
    const main = mk('cc-tour-mock-main')
    main.append(...[0, 1, 2, 3].map(() => document.createElement('i')))
    mock.append(side, main)
    return mock
}

/** Miniature of the header bar with the dropdown open under the focused control. */
function headerMock(focus: 'search' | 'plus' | 'avatar', preview: PreviewRow[], hideQuickAdd = false): DocumentFragment {
    const bar = mk('cc-tour-mock-hdr')
    const search = mk('cc-tour-mock-search' + (focus === 'search' ? ' is-focus' : ''))
    render(h(Search, { size: 13, 'aria-hidden': 'true' }), search)
    // Search step shows a typed query; the real search has no dropdown, Enter
    // opens the results page, so the panel below mirrors that page's tabs.
    search.append(document.createTextNode(focus === 'search' ? 'Acme' : 'Search accounts, suppliers, products…'))
    const kbd = document.createElement('kbd')
    kbd.textContent = '⌘K'
    search.append(kbd)
    const plus = mk('cc-tour-mock-plus' + (focus === 'plus' ? ' is-focus' : ''))
    render(h(Plus, { size: 14, 'aria-hidden': 'true' }), plus)
    const avatar = mk('cc-tour-mock-avatar' + (focus === 'avatar' ? ' is-focus' : ''), 'A')
    // Search step: just the field, no + / avatar beside it.
    bar.append(search, ...(focus === 'search' ? [] : hideQuickAdd ? [avatar] : [plus, avatar]))
    const drop = mk('cc-tour-mock-drop at-' + focus)
    if (focus === 'search') {
        const tabs = mk('cc-tour-mock-tabs')
        tabs.append(...['Accounts', 'Suppliers', 'Products'].map((t, i) => mk('cc-tour-mock-tab' + (i === 0 ? ' is-active' : ''), `${t} 1`)))
        drop.append(mk('cc-tour-mock-results-title', 'Search Results'), tabs)
    }
    drop.append(...previewRows(preview))
    const frag = document.createDocumentFragment()
    frag.append(bar, drop)
    return frag
}

/** Card header: tinted band with a mock of the chrome the step points at. */
function stepHeader(step: TourStep): HTMLElement {
    const { hero, preview = [], mock = 'sidebar', hideQuickAdd } = step.data ?? {}
    const head = mk('cc-tour-head' + (hero ? ' cc-tour-head-hero' : ''))
    head.append(mock === 'sidebar' ? sidebarMock(preview) : headerMock(mock, preview, hideQuickAdd))
    return head
}

let active: Driver | null = null
/** Unregisters the router hook that closes the tour when leaving app pages. */
let stopWatchingRoutes: (() => void) | null = null

/** Pages the tour may run on: the role's area, minus the setup wizard. */
export function isTourPage(path: string, prefix: string): boolean {
    return path.startsWith(prefix) && !/\/onboarding(\/|$)/.test(path)
}

/** Close the tour without marking it seen (the user didn't choose to exit). */
function stop() {
    stopWatchingRoutes?.()
    stopWatchingRoutes = null
    document.body.classList.remove('cc-tour-passive')
    active?.destroy() // destroy() skips onDestroyStarted, so no "seen" flag
    active = null
}

export function useTour() {
    const router = useRouter()
    const userStore = useUserStore()

    const tour = computed(() => tourForRole(userStore.role))

    function shouldAutoStart(path: string): boolean {
        const t = tour.value
        const uid = userStore.user?.id
        if (!t || !uid || userStore.isImpersonating) return false
        if (!isTourPage(path, t.routePrefix)) return false
        if (window.innerWidth < MIN_TOUR_WIDTH) return false
        return !isTourDone(t.id, uid)
    }

    function start(trigger: TourTrigger = 'manual') {
        const t = tour.value
        const uid = userStore.user?.id
        const role = userStore.role
        if (!t || !uid || !role || active?.isActive()) return

        // Drop same-page steps whose target is absent (tier-locked/hidden nav)
        // so the "n of total" count is honest. Route steps are resolved later
        // by Driver's own waitForElement/skipMissingElement.
        const steps = t.steps.filter(s =>
            s.route || typeof s.element !== 'string' || document.querySelector(s.element))
        if (!steps.length) return

        const base = { tour_id: t.id, role, trigger }

        const navTo = async (step: TourStep | undefined) => {
            if (step?.route && router.currentRoute.value.path !== step.route) await router.push(step.route)
        }

        // Any exit (Done, Skip, Esc, overlay) counts as seen — never nag again.
        const finish = (drv: Driver, reason: string | undefined, index: number | undefined) => {
            markTourDone(t.id, uid)
            stopWatchingRoutes?.()
            stopWatchingRoutes = null
            document.body.classList.remove('cc-tour-passive')
            if (reason === 'done') trackEvent('tour_completed', base)
            else trackEvent('tour_skipped', { ...base, step_index: index ?? 0, reason })
            drv.destroy()
            active = null
        }

        // Content steps = everything except hero cards; drives the "n of N" counter.
        const content = steps.filter(s => !s.data?.hero)

        const d = driver({
            steps,
            showProgress: true,
            showButtons: ['next', 'close'], // no Back, Slack-style
            disableActiveInteraction: true,
            popoverClass: 'cc-tour',
            stagePadding: 6,
            stageRadius: 10,
            overlayOpacity: 0.6,
            skipMissingElement: true,
            waitForElement: 3000,
            nextBtnText: 'Next',
            doneBtnText: 'Done',
            onPopoverRender: (popover, { driver: drv, index }) => {
                const step = steps[index ?? 0]
                if (!step) return
                popover.wrapper.prepend(stepHeader(step))
                if (step.data?.hero) {
                    popover.progress.remove()
                } else {
                    const at = content.indexOf(step) + 1
                    popover.progress.textContent = `${at} of ${content.length}`
                    popover.progress.setAttribute('aria-label', `Step ${at} of ${content.length}`)
                }
                if (!drv.isLastStep()) {
                    const later = document.createElement('button')
                    later.type = 'button'
                    later.className = 'cc-tour-skip'
                    later.textContent = 'Maybe later'
                    later.onclick = () => finish(drv, 'skip', index)
                    popover.footerButtons.prepend(later)
                }
            },
            onHighlightStarted: (_el, step, { index }) => {
                // New users (auto start): the welcome card floats with no dark
                // overlay and the page stays usable. The overlay returns on the
                // first real step. Manual replay keeps the overlay throughout.
                document.body.classList.toggle('cc-tour-passive',
                    trigger === 'auto' && index === 0 && !!(step as TourStep).data?.hero)
                trackEvent('tour_step_viewed', { ...base, step_index: index ?? 0, step_title: step.popover?.title })
            },
            onNextClick: async (_el, _step, { driver: drv }) => {
                await navTo(drv.getNextStep() as TourStep | undefined)
                drv.moveNext()
            },
            onPrevClick: async (_el, _step, { driver: drv, index }) => {
                await navTo(steps[(index ?? 0) - 1])
                drv.movePrevious()
            },
            onDestroyStarted: (_el, _step, { driver: drv, index, reason }) => finish(drv, reason, index),
        })

        active = d
        // Leaving the app area (logout, login page, setup wizard) closes the tour.
        stopWatchingRoutes?.()
        stopWatchingRoutes = router.afterEach(to => {
            if (!userStore.user || !isTourPage(to.path, t.routePrefix)) stop()
        })
        trackEvent('tour_started', { ...base, step_count: steps.length })
        d.drive()
    }

    function restart() {
        const t = tour.value
        const uid = userStore.user?.id
        if (!t || !uid) return
        stop()
        markTourDone(t.id, uid, false)
        start('manual')
    }

    return { tour, shouldAutoStart, start, restart, stop }
}
